import { useEffect, useState } from "react";
import { Tag, Plus, Trash2, Power, Check, Loader2, Percent, IndianRupee } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import api from "../services/api";

const blank = {
  code: "",
  discount_type: "percent",
  discount_pct: 10,
  discount_amount: 100,
  max_discount: 500,
  valid_until: "",
  max_uses: "",
  description: "",
  active: true,
};

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blank);
  const [creating, setCreating] = useState(false);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/admin/coupons");
      setCoupons(data.coupons || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load coupons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) {
      toast.error("Coupon code is required");
      return;
    }
    try {
      setCreating(true);
      const payload = {
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        max_discount: form.discount_type === "percent" ? Number(form.max_discount) || null : null,
        discount_pct: form.discount_type === "percent" ? Number(form.discount_pct) || null : null,
        discount_amount: form.discount_type === "flat" ? Number(form.discount_amount) || null : null,
        valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        description: form.description.trim() || null,
        active: !!form.active,
      };
      await api.post("/admin/coupons", payload);
      toast.success(`Coupon ${payload.code} created`);
      setForm(blank);
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create coupon");
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (code, currentActive) => {
    try {
      await api.put(`/admin/coupons/${code}`, { active: !currentActive });
      toast.success(`Coupon ${code} ${!currentActive ? "enabled" : "disabled"}`);
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete coupon ${code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/coupons/${code}`);
      toast.success(`Coupon ${code} deleted`);
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const formatDiscount = (c) => {
    if (c.discount_type === "flat") return `INR ${c.discount_amount} off`;
    return `${c.discount_pct}% off${c.max_discount ? ` (max INR ${c.max_discount})` : ""}`;
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-coupons-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
            <Tag className="h-5 w-5 text-[#FF6B00]" />
          </div>
          <div>
            <h1 className="font-['Outfit'] text-2xl font-bold text-slate-900">Coupons</h1>
            <p className="text-xs text-slate-500">Create promo codes that any customer can apply at checkout.</p>
          </div>
        </div>
      </div>

      {/* Create form */}
      <form onSubmit={handleCreate} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid="coupon-create-form">
        <div className="flex items-center gap-2">
          <Plus className="h-4 w-4 text-emerald-600" />
          <h2 className="font-['Outfit'] font-bold text-slate-900">New Coupon</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-semibold text-slate-700">Coupon Code</Label>
            <Input
              type="text"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="e.g. WELCOME10"
              maxLength={32}
              className="mt-1 rounded-xl h-10 uppercase tracking-wider"
              data-testid="coupon-code-input"
              required
            />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Discount Type</Label>
            <div className="grid grid-cols-2 gap-2 mt-1 h-10">
              <button
                type="button"
                onClick={() => setForm({ ...form, discount_type: "percent" })}
                className={`flex items-center justify-center gap-1.5 rounded-xl border-2 text-xs font-semibold transition-all ${
                  form.discount_type === "percent"
                    ? "border-[#FF6B00] bg-orange-50/60 text-[#FF6B00]"
                    : "border-slate-200 text-slate-500"
                }`}
                data-testid="discount-type-percent"
              >
                <Percent className="h-3.5 w-3.5" /> Percent
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, discount_type: "flat" })}
                className={`flex items-center justify-center gap-1.5 rounded-xl border-2 text-xs font-semibold transition-all ${
                  form.discount_type === "flat"
                    ? "border-[#FF6B00] bg-orange-50/60 text-[#FF6B00]"
                    : "border-slate-200 text-slate-500"
                }`}
                data-testid="discount-type-flat"
              >
                <IndianRupee className="h-3.5 w-3.5" /> Flat
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {form.discount_type === "percent" ? (
            <>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Discount %</Label>
                <Input
                  type="number"
                  step="1"
                  min="1"
                  max="100"
                  value={form.discount_pct}
                  onChange={(e) => setForm({ ...form, discount_pct: e.target.value })}
                  className="mt-1 rounded-xl h-10"
                  data-testid="discount-pct-input"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Max Discount (₹)</Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={form.max_discount}
                  onChange={(e) => setForm({ ...form, max_discount: e.target.value })}
                  className="mt-1 rounded-xl h-10"
                  placeholder="Cap on % discount"
                  data-testid="max-discount-input"
                />
              </div>
            </>
          ) : (
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold text-slate-700">Flat Discount (₹)</Label>
              <Input
                type="number"
                step="1"
                min="1"
                value={form.discount_amount}
                onChange={(e) => setForm({ ...form, discount_amount: e.target.value })}
                className="mt-1 rounded-xl h-10"
                placeholder="e.g. 100 = ₹100 off"
                data-testid="discount-amount-input"
              />
            </div>
          )}
          <div>
            <Label className="text-xs font-semibold text-slate-700">Max Uses (total)</Label>
            <Input
              type="number"
              step="1"
              min="0"
              value={form.max_uses}
              onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
              className="mt-1 rounded-xl h-10"
              placeholder="Unlimited if blank"
              data-testid="max-uses-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-semibold text-slate-700">Valid Until</Label>
            <Input
              type="date"
              value={form.valid_until}
              onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
              className="mt-1 rounded-xl h-10"
              data-testid="valid-until-input"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Description (optional)</Label>
            <Input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. Welcome offer for new customers"
              maxLength={200}
              className="mt-1 rounded-xl h-10"
              data-testid="description-input"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={creating}
          className="bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl h-10 px-5 font-semibold shadow-sm shadow-[#FF6B00]/20"
          data-testid="create-coupon-btn"
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
          Create Coupon
        </Button>
      </form>

      {/* List */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-['Outfit'] font-bold text-slate-900">Active Coupons ({coupons.length})</h2>
        </div>
        {loading ? (
          <div className="py-12 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-slate-300" />
          </div>
        ) : coupons.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-400" data-testid="coupons-empty">
            No coupons yet. Create your first promo code above.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="coupons-table">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-100">
                  <th className="pb-2 font-semibold">CODE</th>
                  <th className="pb-2 font-semibold">DISCOUNT</th>
                  <th className="pb-2 font-semibold">USED / MAX</th>
                  <th className="pb-2 font-semibold">VALID UNTIL</th>
                  <th className="pb-2 font-semibold">STATUS</th>
                  <th className="pb-2 font-semibold text-right">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((c) => (
                  <tr key={c.code} className="border-b border-slate-50 last:border-0" data-testid={`coupon-row-${c.code}`}>
                    <td className="py-3">
                      <div className="font-mono font-bold text-slate-900">{c.code}</div>
                      {c.description && <div className="text-[10px] text-slate-400 mt-0.5">{c.description}</div>}
                    </td>
                    <td className="py-3 text-slate-700">{formatDiscount(c)}</td>
                    <td className="py-3 text-slate-700">
                      {c.used_count ?? 0} / {c.max_uses ?? "∞"}
                    </td>
                    <td className="py-3 text-slate-600">
                      {c.valid_until ? new Date(c.valid_until).toLocaleDateString() : "Never expires"}
                    </td>
                    <td className="py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold ${
                          c.active
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                            : "bg-slate-100 text-slate-500 border border-slate-200"
                        }`}
                      >
                        {c.active ? <Check className="h-2.5 w-2.5" /> : <Power className="h-2.5 w-2.5" />}
                        {c.active ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td className="py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => handleToggle(c.code, c.active)}
                          className="h-8 px-2 rounded-lg text-xs"
                          data-testid={`coupon-toggle-${c.code}`}
                        >
                          <Power className="h-3 w-3 mr-1" /> {c.active ? "Disable" : "Enable"}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => handleDelete(c.code)}
                          className="h-8 px-2 rounded-lg text-xs border-rose-200 text-rose-600 hover:bg-rose-50"
                          data-testid={`coupon-delete-${c.code}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
